/**
 * Insert ▸ Text ▸ Insert File… — bring the text of a document into the publication.
 *   - .txt / .md / .html are read in the browser
 *   - .docx / .doc / .rtf / .odt / .wpd are converted by the backend (LibreOffice → HTML)
 * The result is sanitised HTML (only the tags/styles the editor keeps) plus its plain text.
 */
import axios from 'axios';
import { API, apiErrorMessage } from './projectApi';
import { sanitizeHtml, htmlToText, escapeHtml } from './textUtils';

export const BROWSER_TEXT_EXTENSIONS = ['.txt', '.md', '.markdown', '.html', '.htm'];
export const SERVER_TEXT_EXTENSIONS = ['.docx', '.doc', '.rtf', '.odt', '.wpd'];
export const INSERT_FILE_ACCEPT = [...BROWSER_TEXT_EXTENSIONS, ...SERVER_TEXT_EXTENSIONS].join(',');
const MAX_BYTES = 20 * 1024 * 1024;

const extOf = (name = '') => { const m = /\.[^.]+$/.exec(name.toLowerCase()); return m ? m[0] : ''; };

/** Plain text → editor HTML (one <div> per line, blank lines kept). */
const plainToHtml = (text) => String(text ?? '').replace(/\r\n?/g, '\n').split('\n').map((l) => `<div>${escapeHtml(l) || '<br>'}</div>`).join('');

/** Small Markdown subset (headings, emphasis, lists, links, code, paragraphs) → editor HTML. */
export const markdownToHtml = (md) => {
  const inline = (s) => escapeHtml(s)
    .replace(/`([^`]+)`/g, '<span style="font-family: Roboto Mono">$1</span>')
    .replace(/\*\*([^*]+)\*\*|__([^_]+)__/g, (m, a, b) => `<b>${a ?? b}</b>`)
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<i>$2</i>')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1<i>$2</i>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>');
  const sizes = { 1: 32, 2: 26, 3: 22, 4: 18, 5: 16, 6: 14 };
  const lines = String(md ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let list = null; // { type: 'ul' | 'ol', items: [] }
  let para = [];
  const flushPara = () => { if (para.length) { out.push(`<div>${inline(para.join(' '))}</div>`); para = []; } };
  const flushList = () => { if (list) { out.push(`<${list.type}>${list.items.map((it) => `<li>${inline(it)}</li>`).join('')}</${list.type}>`); list = null; } };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*+]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) { flushPara(); flushList(); out.push('<div><br></div>'); continue; }
    if (h) { flushPara(); flushList(); out.push(`<div style="font-size: ${sizes[h[1].length]}px; font-weight: bold">${inline(h[2])}</div>`); continue; }
    if (ul || ol) {
      flushPara();
      const type = ul ? 'ul' : 'ol';
      if (!list || list.type !== type) { flushList(); list = { type, items: [] }; }
      list.items.push((ul || ol)[1]);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { flushPara(); flushList(); out.push('<div>———</div>'); continue; }
    flushList();
    para.push(line.replace(/^>\s?/, ''));
  }
  flushPara(); flushList();
  // collapse runs of blank lines
  return out.join('').replace(/(<div><br><\/div>){3,}/g, '<div><br></div><div><br></div>');
};

/** Body inner HTML of an HTML document with scripts/styles/head removed (mirrors the backend's cleanup). */
const htmlBody = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style, head, title, meta, link, iframe, object, embed, noscript, svg, img, video, audio, canvas, form, input, button').forEach((n) => n.remove());
  doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((h) => {
    const sizes = { H1: 32, H2: 26, H3: 22, H4: 18, H5: 16, H6: 14 };
    const div = doc.createElement('div');
    div.setAttribute('style', `font-size: ${sizes[h.tagName]}px; font-weight: bold`);
    div.innerHTML = h.innerHTML;
    h.replaceWith(div);
  });
  doc.querySelectorAll('pre').forEach((pre) => { const div = doc.createElement('div'); div.innerHTML = pre.innerHTML.replace(/\r?\n/g, '<br>'); pre.replaceWith(div); });
  // <p align="center"> → inline text-align (the editor keeps the style, not the attribute)
  doc.querySelectorAll('[align]').forEach((el) => { const a = (el.getAttribute('align') || '').toLowerCase(); if (/^(left|center|right|justify)$/.test(a)) el.style.textAlign = a; el.removeAttribute('align'); });
  doc.querySelectorAll('td, th').forEach((c) => { c.querySelectorAll('p, div').forEach((b) => { while (b.firstChild) b.parentNode.insertBefore(b.firstChild, b); b.remove(); }); c.append('\t'); });
  doc.querySelectorAll('tr').forEach((r) => r.append(doc.createElement('br')));
  const out = doc.body ? doc.body.innerHTML : html;
  // Source-line breaks inside the markup are plain spaces (the editor keeps whitespace literally); whitespace between
  // blocks would become stray blank lines; pt sizes → px.
  return out
    .replace(/[ \t]*\r?\n[ \t\r\n]*/g, ' ')
    .replace(/(<(?:p|div|li)\b[^>]*>)\s+/gi, '$1')
    .replace(/\s+(<\/(?:p|div|li)>)/gi, '$1')
    .replace(/(<\/(?:p|div|li|ul|ol)>|<br\s*\/?>|<(?:ul|ol)\b[^>]*>)\s+/gi, '$1')
    .replace(/\s+(<\/(?:ul|ol)>)/gi, '$1')
    .replace(/(font-size\s*:\s*)(-?[\d.]+)pt/gi, (m, a, n) => `${a}${Math.round((parseFloat(n) * 4) / 3 * 100) / 100}px`);
};

const readFileText = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result || ''));
  r.onerror = () => rej(new Error('The file could not be read.'));
  r.readAsText(file);
});

/**
 * Convert a File into { html, text, warnings, filename }. Throws Error(message) on failure.
 * `onProgress(phase)` receives 'reading' | 'converting'.
 */
export const importTextFile = async (file, { onProgress } = {}) => {
  const ext = extOf(file.name);
  if (!BROWSER_TEXT_EXTENSIONS.includes(ext) && !SERVER_TEXT_EXTENSIONS.includes(ext)) {
    throw new Error(`“${file.name}” is not a supported text document. Use ${INSERT_FILE_ACCEPT.split(',').join(', ')}.`);
  }
  if (file.size > MAX_BYTES) throw new Error('The file is larger than the 20 MB limit.');
  if (BROWSER_TEXT_EXTENSIONS.includes(ext)) {
    onProgress?.('reading');
    const raw = await readFileText(file);
    if (!raw.trim()) throw new Error('The file is empty.');
    const html = ext === '.html' || ext === '.htm' ? htmlBody(raw) : ext === '.txt' ? plainToHtml(raw) : markdownToHtml(raw);
    const clean = sanitizeHtml(html);
    return { html: clean, text: htmlToText(clean), warnings: [], filename: file.name };
  }
  onProgress?.('converting');
  const fd = new FormData();
  fd.append('file', file, file.name);
  try {
    const { data } = await axios.post(`${API}/convert/text-import`, fd, { timeout: 120000 });
    const clean = sanitizeHtml(data.html || '');
    if (!htmlToText(clean).trim()) throw new Error('No text was found in the document.');
    return { html: clean, text: htmlToText(clean), warnings: data.warnings || [], filename: data.filename || file.name };
  } catch (e) {
    if (e instanceof Error && !e.response && !e.request) throw e;
    throw new Error(await apiErrorMessage(e, 'The document could not be converted.'));
  }
};

/** Open the OS file picker for a text document and resolve with the chosen File (null when cancelled). */
export const pickTextDocument = () => new Promise((res) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = INSERT_FILE_ACCEPT;
  input.style.display = 'none';
  input.setAttribute('data-testid', 'insert-file-input');
  let done = false;
  const finish = (f) => { if (done) return; done = true; setTimeout(() => input.remove(), 0); res(f); };
  input.onchange = () => finish(input.files && input.files[0] ? input.files[0] : null);
  if (typeof input.oncancel !== 'undefined') input.oncancel = () => finish(null);
  document.body.appendChild(input);
  input.click();
});
